import { type FC, useEffect, useState } from "react"
import { createPortal } from "react-dom"
import type { VisibleBySlot } from "appsShared/inAppMessages"
import { InAppMessageCard } from "./InAppMessageCard"
import { useInAppMessageClose } from "./useInAppMessageClose"

type InlineItem = VisibleBySlot["inline"][number]

/**
 * The element marked `data-ui="<id>"`, found by attribute comparison rather than a CSS
 * selector: the id comes from the snapshot, and a selector built from it could be malformed.
 */
export function findUiSurface(id: string): Element | null {
  for (const el of Array.from(document.querySelectorAll("[data-ui]")))
    if (el.getAttribute("data-ui") === id) return el
  return null
}

/**
 * Renders next to (or inside the top of) the anchor surface. The engine only returns an inline
 * message whose anchor id is in `page.uiSurfaces`; the anchor lookup here re-checks the DOM.
 */
const InAppInlineMessage: FC<{ item: InlineItem; surfacesKey: string }> = ({
  item,
  surfacesKey,
}) => {
  const { dismissHandler } = useInAppMessageClose()
  const [host, setHost] = useState<HTMLElement | null>(null)
  const { message, placement } = item
  const anchor = placement.type === "inline" ? placement.anchor : undefined

  useEffect(() => {
    const el = anchor && findUiSurface(anchor.uiSurface)
    if (!anchor || !el) {
      setHost(null)
      return
    }
    const node = document.createElement("div")
    node.setAttribute("data-ui", "UI-SL2W-288")
    if (anchor.position === "before") el.parentNode?.insertBefore(node, el)
    else if (anchor.position === "after")
      el.parentNode?.insertBefore(node, el.nextSibling)
    else el.insertBefore(node, el.firstChild)
    setHost(node)
    return () => {
      node.remove()
    }
  }, [anchor?.uiSurface, anchor?.position, surfacesKey])

  if (!host) return null
  return createPortal(
    <InAppMessageCard message={message} onClose={dismissHandler(message)} />,
    host,
  )
}

export const InAppInlineSlot: FC<{
  visible: VisibleBySlot["inline"]
  surfacesKey: string
}> = ({ visible, surfacesKey }) => (
  <>
    {visible.map((item) => (
      <InAppInlineMessage
        key={item.message.id}
        item={item}
        surfacesKey={surfacesKey}
      />
    ))}
  </>
)
